"use client";

import { useState, useEffect, useCallback } from "react";

/**
 * Hook to provide voice briefing using Web Speech API.
 * Primarily used for reading Rashifal and Kundali reports.
 */
export const useJyotishiVoice = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const stop = useCallback(() => {
    if (typeof window !== "undefined") {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  const speak = useCallback((text: string) => {
    if (typeof window === "undefined" || !text) return;

    if (isSpeaking) {
      stop();
      return;
    }

    // Clean markdown for better speech
    const textToSpeak = text
      .replace(/[#*`_~]/g, "")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

    const utterance = new SpeechSynthesisUtterance(textToSpeak);

    // Try to find a Hindi voice if available
    const voices = window.speechSynthesis.getVoices();
    const hindiVoice = voices.find((v) => v.lang.startsWith("hi"));
    if (hindiVoice) {
      utterance.voice = hindiVoice;
    } else {
      // Fallback: search for any voice that might be Hindi if prefix doesn't match
      const hiVoice = voices.find((v) => v.lang.includes("hi-IN") || v.name.toLowerCase().includes("hindi"));
      if (hiVoice) utterance.voice = hiVoice;
    }

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  }, [isSpeaking, stop]);

  return { speak, stop, isSpeaking };
};
